import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * DesignContext — which design language the app renders in.
 *
 *   'classic'  the look shipped before 2026-09-10 (no attribute on <html>)
 *   'studio'   the Studio design language (html[data-design="studio"])
 *
 * Everything visual hangs off that one attribute (src/styles/studio.css).
 * `isStudio` is only for the handful of places where the structure — not the
 * colour — differs; see docs/app-map/102-studio-rollout-for-opus.md §1.
 *
 * Storage mirrors ThemeContext: localStorage first (so the pre-paint script in
 * public/index.html can read it with no flash), then the profile
 * (user_profiles.design_variant) so the choice follows the user across devices.
 */

const STORAGE_KEY = 'worship-design';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const DesignContext = createContext({
  design: 'classic',
  isStudio: false,
  setDesign: () => {},
  syncFromServer: () => {},
});

const normalize = (value) => (value === 'studio' ? 'studio' : 'classic');

const applyDesign = (variant) => {
  const root = document.documentElement;
  if (variant === 'studio') root.setAttribute('data-design', 'studio');
  else root.removeAttribute('data-design');
};

export const DesignProvider = ({ children }) => {
  const [design, setDesignState] = useState(() => {
    try {
      return normalize(localStorage.getItem(STORAGE_KEY));
    } catch {
      return 'classic';
    }
  });

  // Set by setDesign(): once the user has chosen in this session, a later
  // profile fetch must not overwrite the choice.
  const chosenLocally = useRef(false);

  useEffect(() => {
    applyDesign(design);
  }, [design]);

  const setDesign = useCallback((next, userId) => {
    const variant = normalize(next);
    chosenLocally.current = true;
    try { localStorage.setItem(STORAGE_KEY, variant); } catch { /* private mode */ }
    applyDesign(variant);
    setDesignState(variant);
    if (userId && API_URL) {
      fetch(`${API_URL}/api/auth/user-preferences/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ design_variant: variant }),
      }).catch(() => {});
    }
  }, []);

  /** Reconcile with the profile once the signed-in user is known. */
  const syncFromServer = useCallback((userId) => {
    if (!userId || !API_URL) return;
    fetch(`${API_URL}/api/auth/user-preferences/${userId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((prefs) => {
        if (!prefs || chosenLocally.current) return;
        const remote = normalize(prefs.design_variant);
        if (prefs.design_variant == null) return;
        try { localStorage.setItem(STORAGE_KEY, remote); } catch { /* private mode */ }
        applyDesign(remote);
        setDesignState(remote);
      })
      .catch(() => {});
  }, []);

  return (
    <DesignContext.Provider value={{ design, isStudio: design === 'studio', setDesign, syncFromServer }}>
      {children}
    </DesignContext.Provider>
  );
};

export const useDesign = () => useContext(DesignContext);

export default DesignContext;
